import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, filter, first, map, Observable, Subject, takeUntil, zip } from 'rxjs';
import * as fromProcess from '../../store/process/process.reducer';
import * as fromRemote from '../../store/remote/remote.reducer';
import { formatPhoneNumber, getFirstArrayValue, isEmpty } from '../../utils';
import { ApiState } from '../../model/api-state.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProcessService } from '../../services/process.service';

const SMS_TOKEN_COUNTER_INITIAL_VALUE = 0;
const SMS_TOKEN_COUNTER_INCREMENT_VALUE = 1;
const SMS_TOKEN_COUNTER_INTERVAL = 180000;

@Component({
  selector: 'app-remote-id-otp',
  templateUrl: './remote-id-otp.component.html',
  styleUrls: ['./remote-id-otp.component.scss'],
})
export class RemoteIdOtpComponent implements OnInit, OnDestroy {
  id$ = this.activatedRoute.params.pipe(map(({ id }) => (id ? String(id) : null)));
  sentSmsTokenState$ = this.store.select(fromRemote.selectSendSmsTokenState);
  sentSmsTokenError$ = this.store.select(fromRemote.selectSendSmsTokenError);
  verifyOtpState$ = this.store.select(fromRemote.selectVerifySmsTokenState);
  verifyOtpStatus$ = this.store.select(fromRemote.selectVerifySmsTokenStatus);
  verifyOtpError$ = this.store.select(fromRemote.selectVerifySmsTokenError);
  verifyOtpIsOk$ = this.store.select(fromRemote.selectVerifySmsTokenIsOk);
  params$ = this.store.select(fromRemote.selectInitParams);
  isActive$ = this.store.select(fromRemote.selectInitIsActive);
  agentName$ = this.params$.pipe(map((params) => params?.agent.agentName));
  agentPhone$ = this.params$.pipe(
    map((params) => (params ? { link: `tel:${params.agent.phoneNumber}`, text: formatPhoneNumber(params.agent.phoneNumber) } : null)),
  );
  agentEmail$ = this.params$.pipe(map((params) => (params ? { link: `mailto:${params.agent.email}`, text: params.agent.email } : null)));

  ngDestroy$ = new Subject<void>();

  smsTokenCounter = SMS_TOKEN_COUNTER_INITIAL_VALUE;
  isSendSmsTokenDisabled = false;

  formGroup = new FormGroup({
    otp: new FormControl('', [Validators.required]),
  });

  apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private processService: ProcessService,
    private activatedRoute: ActivatedRoute,
  ) {}

  get errorDisplay$(): Observable<boolean> {
    return zip([this.verifyOtpState$, this.verifyOtpStatus$]).pipe(
      map(([state, status]) => {
        const form = this.formGroup.controls.otp.touched && this.formGroup.controls.otp.invalid;
        const apiError = state === ApiState.error || (!isEmpty(status) && status !== 'TokenActive');

        return form || apiError;
      }),
    );
  }

  get errorMessage$(): Observable<string> {
    return zip([this.verifyOtpState$, this.verifyOtpStatus$, this.verifyOtpError$]).pipe(
      map(([state, status, error]) => {
        const formErrors = this.formGroup.controls.otp.errors;
        const formErrorsKeys = formErrors ? Object.keys(formErrors).map((key) => `forms.validator.${key}`) : [];
        const firstError = getFirstArrayValue(formErrorsKeys);

        if (firstError) {
          return firstError;
        }

        switch (state) {
          case ApiState.success:
            return `remote.verify.errors.${status || 'Fallback'}`;
          case ApiState.error:
            return `remote.verify.errors.${getFirstArrayValue(error?.errorMessages || []) || 'Fallback'}`;
        }

        return 'remote.verify.errors.Fallback';
      }),
    );
  }

  ngOnInit(): void {
    this.sendSmsToken();
    this.implementVerifyTokenSuccess();
    this.processService.onPageInit();

    this.store.dispatch(fromProcess.actions.setStep({ step: 9 }));
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  sendSmsToken(): void {
    this.id$.pipe(takeUntil(this.ngDestroy$)).subscribe((id) => {
      this.smsTokenCounter += SMS_TOKEN_COUNTER_INCREMENT_VALUE;
      this.isSendSmsTokenDisabled = true;

      setTimeout(() => {
        this.isSendSmsTokenDisabled = false;
      }, SMS_TOKEN_COUNTER_INTERVAL);

      if (!id) {
        this.store.dispatch(fromRemote.actions.sendSmsTokenError({ error: null }));

        return;
      }

      this.store.dispatch(fromRemote.actions.sendSmsTokenStart({ id }));
    });
  }

  verifyOtp(): void {
    this.id$.pipe(takeUntil(this.ngDestroy$)).subscribe((id) => {
      if (!id) {
        this.store.dispatch(fromRemote.actions.verifySmsTokenError({ error: null }));

        return;
      }

      if (!this.formGroup.controls.otp.value) {
        this.formGroup.markAllAsTouched();

        return;
      }

      this.store.dispatch(fromRemote.actions.verifySmsTokenStart({ id, otp: this.formGroup.controls.otp.value }));
    });
  }

  private implementVerifyTokenSuccess(): void {
    combineLatest([this.id$, this.verifyOtpIsOk$])
      .pipe(
        filter(([, isOk]) => isOk),
        first(),
      )
      .subscribe(([id]) => {
        this.formGroup.disable();
        void this.router.navigate(['remote', id, 'consents']);
      });
  }
}
